import React, { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FileUploader } from 'react-drag-drop-files';
import { CustomizedSwitch, TextareaCustom, TextRow } from 'components';
import { Images, INSTRUCTIONS_MAX_LENGTH } from 'utils';

/**
 * The AI block of the Create Template modal.
 *
 * Both inputs are optional and either one is enough: upload a form to transcribe,
 * describe the template you want, or do both — then the document is the base and
 * the description is the set of changes. Requiring the file would have shut out
 * the case with no document to start from, which is most of them.
 *
 * Neither field is marked required, because which one is missing only matters
 * once both are. That check lives on the submit, where it can say so.
 */
export const GenerateWithAi = ({ control, enabled, onToggle, sourceFile, onFileSelect, onFileClear, fileError }) => {
    const [dragging, setDragging] = useState(false);
    const instructions = useWatch({ control, name: 'aiPrompt' }) || '';
    const over = instructions.length > INSTRUCTIONS_MAX_LENGTH;

    return (
        <div className="generate-from-file">
            <div className="generate-from-file-header">
                <div>
                    <p className="generate-from-file-title">Generate with AI</p>
                    <p className="generate-from-file-subtitle">
                        Upload an existing form, describe what you need, or both.
                    </p>
                </div>
                <CustomizedSwitch checked={enabled} handleClick={onToggle} checkedBgColor="#0C66E4" bgColor="#E4E7EC" />
            </div>

            {enabled && (
                <div className="generate-from-file-body">
                    <p className="generate-from-file-label">Upload file</p>

                    {sourceFile ? (
                        <div className="loaded-documents-box generate-from-file-selected">
                            <img src={Images.uploadedFile} alt="icon" />
                            <div className="loaded-document-info-box">
                                <div className="loaded-document-name">
                                    <TextRow name={sourceFile.name} />
                                </div>
                            </div>
                            <button type="button" className="remove-button" onClick={onFileClear}>
                                <img src={Images.grayTrash} alt="remove" />
                            </button>
                        </div>
                    ) : (
                        <FileUploader
                            classes="generate-from-file-uploader"
                            handleChange={onFileSelect}
                            onDraggingStateChange={setDragging}
                            name="sourceFile"
                        >
                            <div className={`generate-from-file-dropzone ${dragging ? 'dragging' : ''} ${fileError ? 'error' : ''}`}>
                                <div className="generate-from-file-dropzone-icon">
                                    <img src={Images.uploadTrayBlue} alt="upload" />
                                </div>
                                <p className="generate-from-file-dropzone-text">
                                    <span>Click to upload</span> or drag and drop
                                </p>
                                <p className="generate-from-file-dropzone-hint">PDF up to 20 MB</p>
                            </div>
                        </FileUploader>
                    )}

                    {!!fileError && <p className="generate-from-file-error">{fileError}</p>}

                    <p className="generate-from-file-label instructions">Instructions</p>
                    <TextareaCustom
                        control={control}
                        name="aiPrompt"
                        classNameWrapper={`generate-from-file-instructions ${over ? 'over-limit' : ''}`}
                        rules={{ maxLength: INSTRUCTIONS_MAX_LENGTH }}
                        placeholder="A daily session note with client details, goals worked on, and a signature at the end."
                        noError
                    />

                    <div className="generate-from-file-footer">
                        <p className="generate-from-file-hint">
                            {over
                                ? 'Shorten the instructions to continue.'
                                : sourceFile
                                  ? 'Describe what you want to keep, remove, add, or change in the generated template.'
                                  : 'Describe the template you want. The more specific you are, the closer the result.'}
                        </p>
                        {/* Always shown, so the author watches it approach the cap rather
                            than discovering it. Turns red past the limit instead of
                            truncating, which would silently drop what they typed. */}
                        <p className={`generate-from-file-counter ${over ? 'over-limit' : ''}`}>
                            {instructions.length.toLocaleString()}/{INSTRUCTIONS_MAX_LENGTH.toLocaleString()}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
